<div class="lds">
  <!-- Modo de reparto. Dos formas porque el criterio cambia según el cliente: a veces un pago cubre un
       arrendamiento entero y a veces se parte a propósito. -->
  <div class="lds-head">
    <div class="lds-seg" role="group" aria-label="Cómo se reparte el pago">
      <button type="button" [class.is-on]="mode === 'CASCADE'" [disabled]="disabled" (click)="setMode('CASCADE')" [attr.aria-pressed]="mode === 'CASCADE'">Cascada</button>
      <button type="button" [class.is-on]="mode === 'EQUAL_PARTS'" [disabled]="disabled" (click)="setMode('EQUAL_PARTS')" [attr.aria-pressed]="mode === 'EQUAL_PARTS'">Partes iguales</button>
    </div>
    <span *ngIf="asOf" class="enf-chip is-muted lds-asof">Mora al {{ asOf | date:'dd/MM/yyyy':'UTC' }} · fecha en que pagó el cliente</span>
  </div>

  <p class="lds-hint">
    <ng-container *ngIf="mode === 'CASCADE'; else hintIguales">El primero se lleva todo lo que debe y el sobrante baja al siguiente. El orden lo pones arrastrando, o con Alt y las flechas.</ng-container>
    <ng-template #hintIguales>El monto se parte en partes iguales entre los marcados. El orden no cambia el reparto.</ng-template>
  </p>

  <!-- El único sitio donde se ve el total repartido -->
  <div class="lds-meter" *ngIf="allocations.length">
    <div class="lds-bar" role="img" [attr.aria-label]="'Se aplican ' + (assigned | currency) + ' de ' + (amount | currency)">
      <span *ngFor="let a of allocations; let i = index" [style.width]="widthOf(a)" [class]="'is-c' + (i % 4)" [pTooltip]="a.folio + ' · ' + (a.amount | currency)" tooltipPosition="top"></span>
      <span *ngIf="leftover > 0.005" class="is-rest" [style.width]="(leftover / amount) * 100 + '%'" [pTooltip]="'Queda disponible en el pago · ' + (leftover | currency)" tooltipPosition="top"></span>
    </div>
    <div class="lds-legend">
      <span>Se aplica <b>{{ assigned | currency }}</b></span>
      <span *ngIf="leftover > 0.005">Sobrante <b class="is-warn">{{ leftover | currency }}</b></span>
      <span>{{ selected.length }} de {{ ordered.length }} arrendamiento(s)</span>
    </div>
  </div>

  <!-- La lista -->
  <div class="lds-list" role="list" aria-label="Arrendamientos y su orden de cobro" *ngIf="ordered.length; else vacio">
    <ng-container *ngFor="let l of ordered; let i = index; trackBy: trackByLease">
      <div
        class="lds-row"
        [class.is-off]="!checked.has(l.leaseId)"
        [class.is-locked]="!selectable(l)"
        [class.is-starved]="!!allocationOf(l)?.starved"
        pDraggable
        pDroppable
        (onDragStart)="onDragStart(i)"
        (onDrop)="onDrop(i)"
        (keydown)="onRowKey($event, i)"
        [attr.tabindex]="selectable(l) ? 0 : -1"
        [attr.aria-disabled]="!selectable(l)"
        role="listitem"
        #fila
        [attr.aria-label]="rowLabel(l, i)">
        <span class="lds-grip" aria-hidden="true"><i class="fa-solid fa-grip-vertical"></i></span>

        <!-- Por `ariaLabel` y NO por `[attr.aria-label]`: el atributo cae sobre el host de PrimeNG, que no
             tiene rol, así que no se expone; el input real lee el @Input. Sin esto el lector anunciaba
             «casilla, no marcada» una vez por fila, sin decir de qué arrendamiento. -->
        <p-checkbox [binary]="true" [ngModel]="checked.has(l.leaseId)" [disabled]="disabled || !selectable(l)" (onChange)="toggle(l)" [ariaLabel]="'Incluir ' + l.folio"></p-checkbox>

        <span class="lds-ord" [class.is-off]="!checked.has(l.leaseId)">{{ i + 1 }}</span>

        <span class="lds-main">
          <span class="lds-folio">{{ l.folio }}</span>
          <span class="lds-sub">
            <span>Vencido <b>{{ l.capacity | currency }}</b></span>
            <span>renta {{ l.rentPending | currency }} · mora <b *ngIf="l.moraPending > 0">{{ l.moraPending | currency }}</b><ng-container *ngIf="l.moraPending <= 0">{{ 0 | currency }}</ng-container></span>
            <span>
              {{ l.openLetters }} letra(s)<ng-container *ngIf="l.oldestOverdueDays !== null"> · la más antigua lleva {{ l.oldestOverdueDays }} días</ng-container>
              <span *ngIf="l.anticipatedCount > 0" class="enf-chip is-muted lds-antic">{{ l.anticipatedCount }} anticipada(s) al final</span>
            </span>
          </span>
        </span>

        <span class="lds-alloc" *ngIf="allocationOf(l) as a">
          <span class="lds-alloc-v" [class.is-muted]="a.starved">{{ a.amount | currency }}</span>
          <span class="lds-alloc-l" [class.is-settle]="a.settles">{{ a.settles ? 'queda saldado' : a.starved ? 'no le llega nada' : 'queda parcial' }}</span>
        </span>
        <span class="lds-alloc" *ngIf="!checked.has(l.leaseId)">
          <span class="lds-alloc-v is-muted">—</span>
          <span class="lds-alloc-l">{{ selectable(l) ? 'excluido' : 'sin saldo que cobrar' }}</span>
        </span>
      </div>

      <!-- Dónde se acaba el dinero. Se recoloca en vivo al reordenar o desmarcar. -->
      <!-- Decorativo para el lector: un `list` solo puede contener `listitem`, y lo que dice ya viaja
           en la región viva al cambiar el reparto. -->
      <div class="lds-cut" role="presentation" *ngIf="showCutAfter(i)">
        <i></i><span>Hasta aquí alcanzan los {{ amount | currency }}</span><i></i>
      </div>
    </ng-container>
  </div>

  <ng-template #vacio>
    <div class="lds-empty">
      <i class="fa-solid fa-file-circle-xmark" aria-hidden="true"></i>
      <p class="lds-empty-t">Este cliente no tiene arrendamientos activos</p>
      <p class="lds-empty-b">El pago se registra igual y queda disponible por aplicar. No va a aparecer en la Exigibilidad de ningún arrendamiento: ahí solo se ven los pagos sin aplicar de ese cliente.</p>
    </div>
  </ng-template>

  <!-- Con dos o más destinos el reparto se confirma: es dinero que se parte. -->
  <div class="lds-confirm" *ngIf="needsConfirmation">
    <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
    <div>
      <p class="lds-confirm-t" *ngIf="receivingCount > 1; else soloUnoRecibe">Este pago se reparte entre {{ receivingCount }} arrendamientos</p>
      <ng-template #soloUnoRecibe>
        <!-- Marcó varios pero el dinero se acaba antes. Decir «se reparte entre 2» cuando uno recibe
             cero es falso, y entrena a marcar la casilla sin leerla. -->
        <p class="lds-confirm-t">De los {{ selected.length }} marcados, el dinero solo alcanza para uno</p>
      </ng-template>
      <p class="lds-confirm-b">
        <ng-container *ngFor="let a of allocations; let last = last"
          ><span class="lds-folio">{{ a.folio }}</span> recibe {{ a.amount | currency }}<ng-container *ngIf="a.settles"> y <b>queda saldado</b></ng-container>{{ last ? '.' : ' · ' }}</ng-container
        >
      </p>
      <!-- El texto va en un `label for`, NO envolviendo al checkbox. Envolviéndolo, un clic sobre la
           caja se contaba dos veces —PrimeNG alterna, y el navegador reenvía el clic al input interno
           por la asociación implícita del label— y la casilla se quedaba igual. Marcarla con el ratón,
           que es el gesto natural, no hacía nada; solo funcionaba con el teclado. Y esta casilla es la
           puerta de todo el flujo. -->
      <div class="lds-confirm-c">
        <p-checkbox [binary]="true" [(ngModel)]="confirmed" [disabled]="disabled" (onChange)="onConfirmChange()" inputId="ldsConfirm" ariaLabel="Entiendo y confirmo el reparto"></p-checkbox>
        <label for="ldsConfirm">Entiendo y confirmo el reparto</label>
      </div>
    </div>
  </div>

  <p class="lds-foot" *ngIf="ordered.length">
    El reparto definitivo, letra por letra, lo calcula el motor al previsualizar el impacto.
  </p>

  <!-- Lo que se anuncia al mover con el teclado -->
  <p class="lds-sr" role="status" aria-live="polite">{{ liveMessage }}</p>
</div>
